export enum ErrorKey {
  // Network errors
  NETWORK_CONNECTION = 'errors.network.connection',
  NETWORK_TIMEOUT = 'errors.network.timeout',
  NETWORK_SERVER_UNREACHABLE = 'errors.network.serverUnreachable',

  // Auth errors
  AUTH_INVALID_CREDENTIALS = 'errors.auth.invalidCredentials',
  AUTH_SESSION_EXPIRED = 'errors.auth.sessionExpired',
  AUTH_UNAUTHORIZED = 'errors.auth.unauthorized',
  AUTH_TOKEN_REFRESH = 'errors.auth.tokenRefresh',

  // Server errors
  SERVER_INTERNAL = 'errors.server.internal',
  SERVER_MAINTENANCE = 'errors.server.maintenance',
  SERVER_UNAVAILABLE = 'errors.server.unavailable',

  // Validation errors
  VALIDATION_REQUIRED = 'errors.validation.required',
  VALIDATION_INVALID_FORMAT = 'errors.validation.invalidFormat',
  VALIDATION_EMAIL = 'errors.validation.email',

  // Fallback
  UNKNOWN = 'errors.unknown',
}

// Context options to help distinguish error types
export type ErrorContext = 'login' | 'refresh' | 'api' | 'general';

interface AxiosErrorResponse {
  response?: {
    status: number;
    data?: any;
  };
  message?: string;
  code?: string;
  isAxiosError?: boolean;
}

function isAxiosError(error: unknown): error is AxiosErrorResponse {
  return (
    error !== null &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as AxiosErrorResponse).isAxiosError === true
  );
}

function getAxiosErrorKey(error: AxiosErrorResponse): string {
  // Network errors (no response received)
  if (!error.response) {
    const errorMessage = (error.message || '').toLowerCase();

    // Check for network/connection issues
    if (
      errorMessage.includes('network error') ||
      errorMessage.includes('err_network') ||
      errorMessage.includes('network')
    ) {
      return ErrorKey.NETWORK_CONNECTION;
    }

    // Check for timeout
    if (
      errorMessage.includes('timeout') ||
      errorMessage.includes('econnaborteded') ||
      error.code === 'ECONNABORTED'
    ) {
      return ErrorKey.NETWORK_TIMEOUT;
    }

    // Check for server unreachable
    if (
      errorMessage.includes('connection refused') ||
      errorMessage.includes('econnrefused') ||
      error.code === 'ECONNREFUSED'
    ) {
      return ErrorKey.NETWORK_SERVER_UNREACHABLE;
    }

    // Default network error for no response
    return ErrorKey.NETWORK_CONNECTION;
  }

  // Server responded with error status
  const status = error.response.status;

  // 4xx errors
  if (status >= 400 && status < 500) {
    // 401 Unauthorized - context matters
    if (status === 401) {
      // For 401, check if it's a login attempt (invalid credentials) or session expiry
      // Login API returns 401 for wrong credentials, but other 401s mean session expired
      // We'll use the error message to distinguish
      const errorMsg = (
        error.response.data?.error?.message ||
        error.response.data?.message ||
        ''
      ).toLowerCase();
      if (
        errorMsg.includes('incorrect') ||
        errorMsg.includes('invalid') ||
        errorMsg.includes('credential')
      ) {
        return ErrorKey.AUTH_INVALID_CREDENTIALS;
      }
      return ErrorKey.AUTH_SESSION_EXPIRED;
    }

    // 403 Forbidden
    if (status === 403) {
      return ErrorKey.AUTH_UNAUTHORIZED;
    }

    // Other 4xx errors as validation
    if (status === 400) {
      return ErrorKey.VALIDATION_INVALID_FORMAT;
    }
  }

  // 5xx errors
  if (status >= 500 && status < 600) {
    // 503 Service Unavailable
    if (status === 503) {
      return ErrorKey.SERVER_UNAVAILABLE;
    }

    // Other 5xx errors
    return ErrorKey.SERVER_INTERNAL;
  }

  return ErrorKey.UNKNOWN;
}

function getStandardErrorKey(error: Error): string {
  const errorMessage = error.message.toLowerCase();

  // Network errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('fetch')
  ) {
    return ErrorKey.NETWORK_CONNECTION;
  }

  // Timeout errors
  if (errorMessage.includes('timeout')) {
    return ErrorKey.NETWORK_TIMEOUT;
  }

  // Auth errors
  if (
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('forbidden') ||
    errorMessage.includes('401') ||
    errorMessage.includes('403')
  ) {
    return ErrorKey.AUTH_UNAUTHORIZED;
  }

  // Validation errors
  if (
    errorMessage.includes('validation') ||
    errorMessage.includes('invalid') ||
    errorMessage.includes('required')
  ) {
    return ErrorKey.VALIDATION_INVALID_FORMAT;
  }

  return ErrorKey.UNKNOWN;
}

export function getErrorKey(error: Error | unknown, context: ErrorContext = 'general'): string {
  // Handle null/undefined
  if (error == null) {
    return ErrorKey.UNKNOWN;
  }

  // Handle Axios errors first (network, server, auth)
  if (isAxiosError(error)) {
    // For login context, 401 should be treated as invalid credentials
    if (context === 'login' && error.response?.status === 401) {
      return ErrorKey.AUTH_INVALID_CREDENTIALS;
    }
    return getAxiosErrorKey(error);
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return getStandardErrorKey(error);
  }

  // Handle string errors
  if (typeof error === 'string') {
    const errorLower = error.toLowerCase();
    if (errorLower.includes('network') || errorLower.includes('connection')) {
      return ErrorKey.NETWORK_CONNECTION;
    }
    if (errorLower.includes('timeout')) {
      return ErrorKey.NETWORK_TIMEOUT;
    }
    if (errorLower.includes('unauthorized') || errorLower.includes('401')) {
      return ErrorKey.AUTH_UNAUTHORIZED;
    }
  }

  // Fallback for unknown error types
  return ErrorKey.UNKNOWN;
}
